import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { api } from '../../services/api';

export const AdminSettingsPage: React.FC = () => {
    const { setFlashMessage } = useOutletContext<{ setFlashMessage: (msg: any) => void }>();
    const [settings, setSettings] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);

    useEffect(() => {
        api.adminGetSettings().then(res => {
            if (res.data?.settings) {
                const map: Record<string, string> = {};
                Object.entries(res.data.settings).forEach(([k, v]) => {
                    map[k] = v || '';
                });
                setSettings(map);
            }
        }).catch(() => {}).finally(() => {
            setLoading(false);
        });
    }, []);

    const handleChange = (key: string, value: string) => {
        setSettings(prev => ({ ...prev, [key]: value }));
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.adminUpdateSettings(settings);
            setFlashMessage({
                type: 'success',
                text: 'Site settings and global SEO configurations saved successfully.'
            });
        } catch (err: any) {
            setFlashMessage({ type: 'error', text: err.message || 'Failed to update settings.' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="py-12 text-center text-slate-400">Loading site settings...</div>;
    }

    return (
        <div className="max-w-4xl mx-auto space-y-8">
            <div>
                <h1 className="text-3xl font-extrabold text-white tracking-tight">Site Settings & SEO</h1>
                <p className="text-sm text-slate-400 mt-1">Configure brand identity, motion intro behavior, social platforms, and global search metadata.</p>
            </div>

            <form onSubmit={handleSave} className="space-y-8">
                {/* Brand Identity */}
                <div className="p-8 rounded-3xl glass-card space-y-6">
                    <h2 className="text-lg font-bold text-white uppercase tracking-wider border-b border-slate-800 pb-3">Brand Identity</h2>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">Brand Title</label>
                            <input 
                                type="text" 
                                value={settings['brand_name'] || "Rakib's Care"}
                                onChange={e => handleChange('brand_name', e.target.value)}
                                className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" 
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">Persona Owner Name</label>
                            <input 
                                type="text" 
                                value={settings['brand_owner'] || "Rakib Hasan"}
                                onChange={e => handleChange('brand_owner', e.target.value)}
                                className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" 
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">Tagline</label>
                        <input 
                            type="text" 
                            value={settings['site_tagline'] || "Leadership • Purpose • Impact"}
                            onChange={e => handleChange('site_tagline', e.target.value)}
                            className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" 
                        />
                    </div>

                    <div>
                        <label className="flex items-center gap-3 cursor-pointer text-xs text-slate-300 pt-2">
                            <input 
                                type="checkbox" 
                                checked={settings['intro_enabled'] !== 'false' && settings['intro_enabled'] !== '0'}
                                onChange={e => handleChange('intro_enabled', e.target.checked ? 'true' : 'false')}
                                className="rounded border-slate-700 bg-slate-900 text-emerald-500 focus:ring-emerald-500" 
                            />
                            <span>Enable GSAP Brand Intro splash overlay on first visitor session</span>
                        </label>
                    </div>
                </div>

                {/* Hero & Narrative Copy */}
                <div className="p-8 rounded-3xl glass-card space-y-6">
                    <h2 className="text-lg font-bold text-white uppercase tracking-wider border-b border-slate-800 pb-3">Hero & Narrative</h2>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">Hero Badge</label>
                            <input 
                                type="text" 
                                value={settings['hero_badge'] || "Official Personal Brand"}
                                onChange={e => handleChange('hero_badge', e.target.value)}
                                className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" 
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">Hero Subtitle</label>
                        <textarea 
                            rows={3}
                            value={settings['hero_subtitle'] || ''}
                            onChange={e => handleChange('hero_subtitle', e.target.value)}
                            className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500" 
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">Short Bio</label>
                        <textarea 
                            rows={3}
                            value={settings['bio_short'] || ''}
                            onChange={e => handleChange('bio_short', e.target.value)}
                            className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500" 
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">Leadership Philosophy Quote</label>
                        <textarea 
                            rows={2}
                            value={settings['leadership_philosophy'] || ''}
                            onChange={e => handleChange('leadership_philosophy', e.target.value)}
                            className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs italic focus:outline-none focus:ring-2 focus:ring-emerald-500" 
                        />
                    </div>
                </div>

                {/* Contact & Social Networks */}
                <div className="p-8 rounded-3xl glass-card space-y-6">
                    <h2 className="text-lg font-bold text-white uppercase tracking-wider border-b border-slate-800 pb-3">Contact & Social Networks</h2>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">Contact Email</label>
                            <input 
                                type="email" 
                                value={settings['contact_email'] || ''}
                                onChange={e => handleChange('contact_email', e.target.value)}
                                className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500" 
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">WhatsApp Link</label>
                            <input 
                                type="text" 
                                value={settings['contact_whatsapp'] || ''}
                                onChange={e => handleChange('contact_whatsapp', e.target.value)}
                                className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500" 
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                        <div>
                            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">Facebook URL</label>
                            <input 
                                type="url" 
                                value={settings['social_facebook'] || ''}
                                onChange={e => handleChange('social_facebook', e.target.value)}
                                className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500" 
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">Instagram URL</label>
                            <input 
                                type="url" 
                                value={settings['social_instagram'] || ''}
                                onChange={e => handleChange('social_instagram', e.target.value)}
                                className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500" 
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">LinkedIn URL</label>
                            <input 
                                type="url" 
                                value={settings['social_linkedin'] || ''}
                                onChange={e => handleChange('social_linkedin', e.target.value)}
                                className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500" 
                            />
                        </div>
                    </div>
                </div>

                {/* Global Search Engine Optimization (SEO) */}
                <div className="p-8 rounded-3xl glass-card space-y-6">
                    <h2 className="text-lg font-bold text-white uppercase tracking-wider border-b border-slate-800 pb-3">Global SEO Metadata</h2>

                    <div>
                        <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">Default Meta Title</label>
                        <input 
                            type="text" 
                            value={settings['meta_title'] || ''}
                            onChange={e => handleChange('meta_title', e.target.value)}
                            className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500" 
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">Default Meta Description</label>
                        <textarea 
                            rows={3}
                            value={settings['meta_description'] || ''}
                            onChange={e => handleChange('meta_description', e.target.value)}
                            className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500" 
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">Keywords (comma separated)</label>
                        <input 
                            type="text" 
                            value={settings['meta_keywords'] || ''}
                            onChange={e => handleChange('meta_keywords', e.target.value)}
                            className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500" 
                        />
                    </div>
                </div>

                <div className="flex justify-end">
                    <button 
                        type="submit" 
                        disabled={saving}
                        className="px-8 py-3 rounded-xl text-xs font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-500 text-white shadow-xl transition cursor-pointer disabled:opacity-50"
                    >
                        {saving ? 'Saving...' : 'Save All Settings'}
                    </button>
                </div>
            </form>
        </div>
    );
};
